import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FileSelectEvent, FileUploadModule } from 'primeng/fileupload';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { catchError, forkJoin, of } from 'rxjs';

import { AuditActorSummary } from 'src/app/shared/audit/audit.models';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { LocationService } from 'src/app/location/location.service';
import { LazyLocationPickerDirective } from 'src/app/shared/location-picker/lazy-location-picker.directive';
import { FirebaseStorageService } from 'src/app/services/firebase-storage.service';
import { PosmDeliverySchedule, PosmProjectStore } from '../../models/posm-execution.models';
import {
    PosmCostingSummary,
    PosmProject,
    PosmProjectAttachment,
    PosmValidationIssue,
    PosmWorkspaceSummary
} from '../../models/posm-project.model';
import { PosmProjectItem, PosmProjectItemSource } from '../../models/posm-project-item.model';
import { PosmSetupData } from '../../models/posm-planning.model';
import { PosmService } from '../../posm.service';

type OverviewStat = {
    label: string;
    value: string | number;
    hint?: string;
};

type OverviewCard = {
    title: string;
    subtitle: string;
    badge: string;
    badgeSeverity: 'success' | 'info' | 'warn' | 'danger' | 'secondary';
    tone: 'default' | 'success' | 'warn' | 'danger' | 'info';
    stats: OverviewStat[];
    note?: string;
};

type MaterialOverviewRow = {
    item: PosmProjectItem;
    requested: number;
    reserved: number;
    allocated: number;
    gap: number;
    status: string;
    detail: string;
};

@Component({
    selector: 'app-posm-project-overview',
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DatePickerModule,
        DialogModule,
        FileUploadModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputNumberModule,
        InputTextModule,
        ProgressBarModule,
        SelectModule,
        LazyLocationPickerDirective,
        TableModule,
        TagModule,
        TextareaModule,
        ToastModule,
        PageDataPanelComponent,
        PageMetricStripComponent
    ],
    providers: [MessageService],
    templateUrl: './posm-project-overview.component.html',
    styleUrls: ['./posm-project-overview.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmProjectOverviewComponent {
    private posmService = inject(PosmService);
    private locationService = inject(LocationService);
    private firebaseStorageService = inject(FirebaseStorageService);
    private messageService = inject(MessageService);

    public project = input.required<PosmProject>();
    public stores = input<PosmProjectStore[]>([]);
    public storesLoading = input<boolean>(false);
    public workspaceSummary = input<PosmWorkspaceSummary | null>(null);
    public onDataChanged = output<void>();

    public projectItems = signal<PosmProjectItem[]>([]);
    public projectItemSources = signal<Record<number, PosmProjectItemSource[]>>({});
    public deliverySchedules = signal<PosmDeliverySchedule[]>([]);
    public attachments = signal<PosmProjectAttachment[]>([]);
    public costingSummary = signal<PosmCostingSummary | null>(null);
    public dataLoading = signal(false);
    public attachmentCategories = signal<PosmSetupData[]>([]);
    public editStoreVisible = signal(false);
    public editStoreDraft: PosmProjectStore = { location: {} };
    public selectedStoreLocationId: number | null = null;
    public editLatitude: number | null = null;
    public editLongitude: number | null = null;
    public attachmentDraft: PosmProjectAttachment = { category: 'STORE_LIST' };
    public attachmentUploadProgress = signal(0);
    public attachmentUploading = signal(false);

    public attachmentCategoryOptions = computed(() => this.toOptions(this.attachmentCategories()));
    public projectCompanyId = computed(() => this.project().owningCompanyId || 0);
    public validationIssues = computed<PosmValidationIssue[]>(() => this.workspaceSummary()?.validationIssues || []);
    public criticalValidationCount = computed(() =>
        this.validationIssues().filter(issue => `${issue.severity || ''}`.toUpperCase() === 'ERROR').length
    );

    public totalRequestedQuantity = computed(() =>
        this.projectItems().reduce((total, item) => total + this.numberValue(item.quantity), 0)
    );
    public totalReservedQuantity = computed(() =>
        Object.values(this.projectItemSources()).flat().reduce((total, source) => total + this.numberValue(source.reservedQuantity), 0)
    );
    public totalAllocatedQuantity = computed(() =>
        this.deliverySchedules().reduce((total, row) => total + this.numberValue(row.allocatedQuantity), 0)
    );
    public reservationGapQuantity = computed(() =>
        Math.max(this.totalRequestedQuantity() - this.totalReservedQuantity(), 0)
    );
    public scheduleCount = computed(() => this.deliverySchedules().length);
    public readyScheduleCount = computed(() =>
        this.deliverySchedules().filter(row => this.normalizeStatus(row.readinessStatus) === 'READY').length
    );
    public releasedScheduleCount = computed(() =>
        this.deliverySchedules().filter(row => this.isReleased(row)).length
    );
    public allocatedScheduleCount = computed(() =>
        this.deliverySchedules().filter(row => this.normalizeStatus(row.allocationStatus) === 'ALLOCATED').length
    );
    public partialAllocationCount = computed(() =>
        this.deliverySchedules().filter(row => this.normalizeStatus(row.allocationStatus) === 'PARTIAL').length
    );
    public activeShipmentCount = computed(() =>
        this.workspaceSummary()?.activeShipments
        ?? this.deliverySchedules().filter(row => this.isActiveTrackingRow(row)).length
    );
    public delayedShipmentCount = computed(() =>
        this.workspaceSummary()?.delayedShipments
        ?? this.deliverySchedules().filter(row => this.isDelayedSchedule(row)).length
    );
    public deliveredScheduleCount = computed(() =>
        this.deliverySchedules().filter(row => this.isDelivered(row)).length
    );
    public recentAttachmentCount = computed(() =>
        this.attachments().filter(attachment => {
            const createdAt = this.toDate(attachment.createdAt);
            if (!createdAt) return false;
            const ageMs = Date.now() - createdAt.getTime();
            return ageMs <= (7 * 24 * 60 * 60 * 1000);
        }).length
    );
    public attachmentCategorySummary = computed(() => {
        const counts = new Map<string, number>();
        this.attachments().forEach(attachment => {
            const label = this.attachmentCategoryLabel(attachment.category);
            counts.set(label, (counts.get(label) || 0) + 1);
        });
        return Array.from(counts.entries())
            .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
            .slice(0, 4)
            .map(([label, count]) => ({ label, count }));
    });
    public fullyReservedSkuCount = computed(() =>
        this.materialRows().filter(row => row.requested > 0 && row.gap <= 0).length
    );
    public reservationSourceCount = computed(() =>
        Object.values(this.projectItemSources()).flat().length
    );
    public reservedSourceCount = computed(() =>
        Object.values(this.projectItemSources()).flat().filter(source => this.numberValue(source.reservedQuantity) > 0).length
    );
    public pendingCostControlCount = computed(() => {
        const costing = this.costingSummary();
        return this.numberValue(costing?.pendingAddonApprovals)
            + this.numberValue(costing?.pendingExternalLaborApprovals)
            + this.numberValue(costing?.unpostedAccountingCount)
            + this.numberValue(costing?.payrollPendingCount);
    });

    public metrics = computed<PageMetric[]>(() => [
        {
            label: 'Required Qty',
            value: this.totalRequestedQuantity(),
            hint: `${this.projectItems().length} SKU lines`,
            tone: 'info'
        },
        {
            label: 'Covered Qty',
            value: this.totalReservedQuantity(),
            hint: `${this.fullyReservedSkuCount()} fully covered`,
            tone: this.reservationGapQuantity() > 0 ? 'warn' : 'success'
        },
        {
            label: 'Allocated Qty',
            value: this.totalAllocatedQuantity(),
            hint: `${this.allocatedScheduleCount()} fully allocated rows`,
            tone: this.partialAllocationCount() > 0 ? 'warn' : 'success'
        },
        {
            label: 'Schedule Rows',
            value: this.scheduleCount(),
            hint: `${this.readyScheduleCount()} ready for release`,
            tone: this.scheduleCount() ? 'info' : 'default'
        },
        {
            label: 'Released Rows',
            value: this.releasedScheduleCount(),
            hint: `${this.deliveredScheduleCount()} delivered`,
            tone: this.releasedScheduleCount() ? 'success' : 'warn'
        },
        {
            label: 'Active Shipments',
            value: this.activeShipmentCount(),
            hint: `${this.delayedShipmentCount()} delayed`,
            tone: this.delayedShipmentCount() ? 'danger' : 'info'
        }
    ]);

    public summaryCards = computed<OverviewCard[]>(() => {
        const project = this.project();
        const actionCount = this.workspaceSummary()?.actionCount || 0;
        const transportErrors = this.workspaceSummary()?.transportReleaseValidationErrors || 0;
        const todayTarget = project.deadline ? new Date(project.deadline).toLocaleDateString() : 'No deadline';

        return [
            {
                title: 'POSM Execution Scope',
                subtitle: 'Execution scope, deadlines, and operating pressure.',
                badge: project.status || 'DRAFT',
                badgeSeverity: this.statusSeverity(project.status),
                tone: actionCount > 0 ? 'warn' : 'info',
                stats: [
                    { label: 'Project Type', value: this.projectJobTypeLabel(project), hint: `${this.projectJobTypeGroup(project)} | ${project.jobPriority || 'Normal priority'}` },
                    { label: 'Target Stores', value: this.stores().length || this.workspaceSummary()?.totalStores || 0, hint: `${this.workspaceSummary()?.storesMissingCoordinates || 0} missing coordinates` },
                    { label: 'Days Remaining', value: this.daysRemaining(), hint: todayTarget },
                    { label: 'Open Actions', value: actionCount, hint: `${this.criticalValidationCount()} critical validation issues` }
                ],
                note: `${project.crmClient?.companyName || project.client?.name || 'Client pending'} | ${project.brandName || 'Brand pending'}`
            },
            {
                title: 'Client & Brand',
                subtitle: 'Commercial ownership and product brand linked to this execution.',
                badge: project.clientAccountName ? 'LINKED' : 'PENDING',
                badgeSeverity: project.clientAccountName ? 'success' : 'warn',
                tone: project.clientAccountName ? 'success' : 'warn',
                stats: [
                    { label: 'CRM Client', value: project.crmClient?.companyName || project.client?.name || 'Not linked', hint: project.crmClient?.clientNumber || 'CRM client pending' },
                    { label: 'CRM Client Account', value: project.clientAccountName || 'Not linked', hint: project.clientAccountName ? 'Account selected for execution' : 'Account selection pending' },
                    { label: 'Brand', value: project.brandName || 'Not specified', hint: project.brandId ? 'Resolved from warehousing catalog' : 'No brand linked' },
                    { label: 'Client Contact', value: project.clientContactName || project.clientContact?.name || 'Not assigned', hint: project.clientContactEmail || project.clientContactPhone || 'Contact pending' }
                ],
                note: project.clientAccountName
                    ? 'Commercial client, account, and brand context is available for this execution.'
                    : 'This execution is missing a linked CRM client account.'
            },
            {
                title: 'Materials & Coverage',
                subtitle: 'Requested stands/materials, physical reservations or logical external coverage, and downstream allocation.',
                badge: this.reservationGapQuantity() > 0 ? 'ATTENTION' : 'COVERED',
                badgeSeverity: this.reservationGapQuantity() > 0 ? 'warn' : 'success',
                tone: this.reservationGapQuantity() > 0 ? 'warn' : 'success',
                stats: [
                    { label: 'SKU Lines', value: this.projectItems().length, hint: `${this.reservationSourceCount()} source lines` },
                    { label: 'Requested Qty', value: this.totalRequestedQuantity(), hint: `${this.reservedSourceCount()} source lines already covered` },
                    { label: 'Covered Qty', value: this.totalReservedQuantity(), hint: `${this.totalAllocatedQuantity()} allocated to tasks` },
                    { label: 'Coverage Gap', value: this.reservationGapQuantity(), hint: `${this.fullyReservedSkuCount()} SKU lines fully covered` }
                ],
                note: this.reservationGapQuantity() > 0
                    ? 'Some materials still need physical reservation or external supply confirmation.'
                    : 'Requested material quantities are fully covered at source level.'
            },
            {
                title: 'Delivery Schedule & Releases',
                subtitle: 'Readiness, allocation, release generation, and schedule execution state.',
                badge: this.scheduleCount() ? `${this.readyScheduleCount()} READY` : 'NO ROWS',
                badgeSeverity: this.readyScheduleCount() > 0 ? 'info' : 'secondary',
                tone: transportErrors > 0 ? 'warn' : 'info',
                stats: [
                    { label: 'Schedule Rows', value: this.scheduleCount(), hint: `${this.partialAllocationCount()} partially allocated` },
                    { label: 'Fully Allocated', value: this.allocatedScheduleCount(), hint: `${Math.max(this.scheduleCount() - this.allocatedScheduleCount(), 0)} not fully allocated` },
                    { label: 'Release Generated', value: this.releasedScheduleCount(), hint: `${transportErrors} release validation issues` },
                    { label: 'Delivered', value: this.deliveredScheduleCount(), hint: `${this.activeShipmentCount()} active shipments` }
                ],
                note: this.releasedScheduleCount() > 0
                    ? 'Delivery rows are moving beyond planning into release and transport execution.'
                    : 'No order releases have been generated yet.'
            },
            {
                title: 'Attachments & Approvals',
                subtitle: 'Store lists, artwork, mockups, approvals, and operating documents.',
                badge: this.attachments().length ? `${this.attachments().length} FILES` : 'NO FILES',
                badgeSeverity: this.attachments().length ? 'info' : 'secondary',
                tone: this.attachmentUploading() ? 'info' : (this.attachments().length ? 'success' : 'default'),
                stats: [
                    { label: 'Files Linked', value: this.attachments().length, hint: `${this.attachmentCategorySummary().length} active categories` },
                    { label: 'Recent Uploads', value: this.recentAttachmentCount(), hint: 'last 7 days' },
                    { label: 'Uploading', value: this.attachmentUploading() ? 'Yes' : 'No', hint: this.attachmentUploading() ? `${this.attachmentUploadProgress()}% complete` : 'idle' },
                    { label: 'Primary Category', value: this.attachmentCategorySummary()[0]?.label || 'Unassigned', hint: `${this.attachmentCategorySummary()[0]?.count || 0} file(s)` }
                ],
                note: 'Keep execution files close to store planning so operators can review lists, artwork, and approvals without leaving the overview.'
            },
            {
                title: 'Audit Trail',
                subtitle: 'Creation, updates, closure, and reopen ownership for this execution record.',
                badge: this.auditBadge(project),
                badgeSeverity: this.auditBadgeSeverity(project),
                tone: this.auditTone(project),
                stats: [
                    {
                        label: 'Created',
                        value: this.formattedDateTime(project.createdAt),
                        hint: this.auditActorLabel(project.createdByActor, project.createdBy, 'No creator recorded')
                    },
                    {
                        label: 'Updated',
                        value: this.formattedDateTime(project.updatedAt),
                        hint: this.auditActorLabel(project.updatedByActor, project.updatedBy, 'No updater recorded')
                    },
                    {
                        label: 'Closed',
                        value: this.formattedDateTime(project.closedAt),
                        hint: this.auditActorLabel(project.closedByActor, project.closedByCustomerId, 'Execution still open')
                    },
                    {
                        label: 'Reopened',
                        value: this.formattedDateTime(project.reopenedAt),
                        hint: this.auditActorLabel(project.reopenedByActor, project.reopenedByCustomerId, 'No reopen activity')
                    }
                ],
                note: this.auditNote(project)
            }
        ];
    });

    public materialRows = computed<MaterialOverviewRow[]>(() =>
        this.projectItems()
            .map(item => {
                const requested = this.numberValue(item.quantity);
                const reserved = this.itemReservedQuantity(item.id);
                const allocated = this.itemAllocatedQuantity(item.id);
                const gap = Math.max(requested - reserved, 0);
                const status = this.materialRowStatus(item, requested, reserved, allocated);
                return {
                    item,
                    requested,
                    reserved,
                    allocated,
                    gap,
                    status,
                    detail: item.materialStatusDetail || this.materialRowDetail(status, gap, requested, reserved, allocated)
                };
            })
            .sort((left, right) => {
                const gapCompare = right.gap - left.gap;
                if (gapCompare !== 0) return gapCompare;
                return this.itemName(left.item).localeCompare(this.itemName(right.item));
            })
    );

    public scheduleHeadlineRows = computed(() =>
        [...this.deliverySchedules()]
            .sort((left, right) => this.scheduleRiskScore(right) - this.scheduleRiskScore(left))
            .slice(0, 8)
    );

    private loadedProjectId?: number;

    constructor() {
        effect(() => {
            const project = this.project();
            if (project?.id && project.id !== this.loadedProjectId) {
                this.loadedProjectId = project.id;
                this.loadProjectData(project);
            }
        });
    }

    loadProjectData(project: PosmProject): void {
        const companyId = this.projectCompanyId();
        if (!project.id || !companyId) return;
        this.dataLoading.set(true);

        forkJoin({
            items: this.posmService.getProjectItems(project.id, companyId).pipe(catchError(() => of([] as PosmProjectItem[]))),
            attachments: this.posmService.getProjectAttachments(project.id, companyId).pipe(catchError(() => of([] as PosmProjectAttachment[]))),
            schedules: this.posmService.getDeliverySchedules(project.id, companyId).pipe(catchError(() => of([] as PosmDeliverySchedule[]))),
            costing: this.posmService.getPosmCostingSummary(project.id, companyId).pipe(catchError(() => of(null))),
            attachmentCategories: this.posmService.getSetup('ATTACHMENT_CATEGORY', companyId).pipe(catchError(() => of([] as PosmSetupData[])))
        }).subscribe({
            next: ({ items, attachments, schedules, costing, attachmentCategories }) => {
                this.projectItems.set(items || []);
                this.attachments.set(attachments || []);
                this.deliverySchedules.set((schedules || []).map(row => this.normalizeSchedule(row)));
                this.costingSummary.set(costing);
                this.attachmentCategories.set(attachmentCategories || []);
                this.attachmentDraft = {
                    category: this.attachmentDraft.category || attachmentCategories?.[0]?.code || 'OTHER'
                };
                this.loadMaterialSources(project.id!, companyId, items || []);
            },
            error: () => {
                this.dataLoading.set(false);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Execution Overview',
                    detail: 'Unable to load the POSM execution overview.'
                });
            }
        });
    }

    private loadMaterialSources(projectId: number, companyId: number, items: PosmProjectItem[]): void {
        const keyedItems = items.filter(item => !!item.id);
        if (!keyedItems.length) {
            this.projectItemSources.set({});
            this.dataLoading.set(false);
            return;
        }

        forkJoin(
            keyedItems.map(item =>
                this.posmService.getProjectItemSources(projectId, item.id!, companyId)
                    .pipe(catchError(() => of([] as PosmProjectItemSource[])))
            )
        ).subscribe({
            next: (sourceGroups) => {
                const next: Record<number, PosmProjectItemSource[]> = {};
                keyedItems.forEach((item, index) => {
                    next[item.id!] = sourceGroups[index] || [];
                });
                this.projectItemSources.set(next);
                this.dataLoading.set(false);
            },
            error: () => {
                this.projectItemSources.set({});
                this.dataLoading.set(false);
            }
        });
    }

    uploadAttachmentFile(event: FileSelectEvent): void {
        const file = event.files?.[0];
        const project = this.project();
        const companyId = this.projectCompanyId();
        if (!file || !project.id || !companyId) return;

        const category = (this.attachmentDraft.category || 'OTHER').toLowerCase().replace(/[^a-z0-9_-]/g, '-');
        const projectRef = project.projectNumber || `${project.id}`;
        const storagePath = `posm-projects/${companyId}/${projectRef}/attachments/${category}`;

        this.attachmentUploading.set(true);
        this.attachmentUploadProgress.set(1);

        this.firebaseStorageService.uploadFileWithProgress(file, storagePath).subscribe({
            next: upload => {
                this.attachmentUploadProgress.set(upload.progress);
                if (!upload.url) return;

                const categoryCode = this.attachmentDraft.category || 'OTHER';
                const payload: PosmProjectAttachment = {
                    category: categoryCode,
                    fileName: file.name,
                    fileUrl: upload.url,
                    fileType: file.type,
                    fileSize: file.size
                };

                this.posmService.saveProjectAttachment(project.id!, companyId, payload).subscribe({
                    next: (saved) => {
                        this.attachments.set([saved, ...this.attachments()]);
                        this.attachmentDraft = { category: categoryCode };
                        this.attachmentUploading.set(false);
                        this.attachmentUploadProgress.set(100);
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Attachment Saved',
                            detail: `${file.name} was uploaded and linked to the project.`
                        });
                        this.onDataChanged.emit();
                    },
                    error: (error) => {
                        this.attachmentUploading.set(false);
                        this.attachmentUploadProgress.set(0);
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Attachment Save Failed',
                            detail: error?.error?.message || error?.error || 'The file uploaded, but the project attachment record could not be saved.'
                        });
                    }
                });
            },
            error: (error) => {
                this.attachmentUploading.set(false);
                this.attachmentUploadProgress.set(0);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Upload Failed',
                    detail: error?.message || 'Unable to upload the file.'
                });
            }
        });
    }

    deleteAttachment(attachment: PosmProjectAttachment): void {
        const project = this.project();
        if (!project.id || !attachment.id || !this.projectCompanyId()) return;
        this.posmService.deleteProjectAttachment(project.id, attachment.id, this.projectCompanyId()).subscribe(() => {
            this.attachments.set(this.attachments().filter(row => row.id !== attachment.id));
            this.onDataChanged.emit();
        });
    }

    openStoreEditor(store: PosmProjectStore): void {
        this.editStoreDraft = JSON.parse(JSON.stringify(store));
        this.editStoreDraft.location = { ...(this.editStoreDraft.location || {}) };
        this.selectedStoreLocationId = this.editStoreDraft.location?.id ?? null;
        this.editStoreDraft.address = store.address || this.locationAddress(store);
        this.editStoreDraft.plannedDate = store.plannedDate ? new Date(store.plannedDate) : undefined;
        this.editLatitude = store.location?.location?.latitude ?? null;
        this.editLongitude = store.location?.location?.longitude ?? null;
        this.editStoreVisible.set(true);
    }

    onStoreLocationChange(locationId: number | null): void {
        this.selectedStoreLocationId = locationId;
        if (!locationId) return;
        this.locationService.getLocationById(locationId).subscribe({
            next: response => {
                if (this.selectedStoreLocationId !== locationId) return;
                const selected = response.data?.[0];
                if (selected) this.applyStoreLocation(selected);
            }
        });
    }

    private applyStoreLocation(selected: any): void {
        this.editStoreDraft.location = { ...selected };
        this.editStoreDraft.storeNumber = this.editStoreDraft.storeNumber || selected.externalLocationReference || '';
        this.editStoreDraft.city = this.editStoreDraft.city || selected.shortDescription || '';
        this.editStoreDraft.contactPersonPhone = this.editStoreDraft.contactPersonPhone || selected.telephoneNumber || '';
        this.editStoreDraft.address = this.locationAddressFromLocation(selected);
        this.editLatitude = selected.location?.latitude ?? selected.latitude ?? null;
        this.editLongitude = selected.location?.longitude ?? selected.longitude ?? null;
    }

    saveStore(): void {
        const project = this.project();
        const storeId = this.editStoreDraft.id;
        if (!project.id || !storeId || !this.projectCompanyId()) return;
        this.editStoreDraft.location = this.editStoreDraft.location || {};
        this.editStoreDraft.location.location = this.editLatitude !== null && this.editLongitude !== null
            ? { latitude: this.editLatitude, longitude: this.editLongitude }
            : null;
        this.posmService.updateProjectStore(project.id, storeId, this.projectCompanyId(), this.editStoreDraft).subscribe({
            next: () => {
                this.editStoreVisible.set(false);
                this.messageService.add({
                    severity: 'success',
                    summary: 'Store Saved',
                    detail: 'Store plan was updated.'
                });
                this.onDataChanged.emit();
            },
            error: () => this.messageService.add({
                severity: 'error',
                summary: 'Store Save Failed',
                detail: 'Unable to update the store.'
            })
        });
    }

    storeName(store: PosmProjectStore): string {
        return store.location?.name || store.storeName || (store.storeNumber ? `Store ${store.storeNumber}` : '-');
    }

    storeArea(store: PosmProjectStore): string {
        return store.city || store.location?.shortDescription || store.location?.longDescription || '-';
    }

    locationReference(store: PosmProjectStore): string {
        return store.location?.externalLocationReference || store.storeNumber || '-';
    }

    locationType(store: PosmProjectStore): string {
        return store.location?.locationType || 'POSM_STORE';
    }

    locationAddress(store: PosmProjectStore): string {
        const address = store.location?.address;
        if (typeof address === 'string') return address;
        return store.address
            || address?.fullAddress
            || store.location?.longDescription
            || store.location?.shortDescription
            || '-';
    }

    locationCoordinateText(store: PosmProjectStore): string {
        const latitude = store.location?.location?.latitude;
        const longitude = store.location?.location?.longitude;
        return latitude != null && longitude != null
            ? `${Number(latitude).toFixed(5)}, ${Number(longitude).toFixed(5)}`
            : 'Missing coordinates';
    }

    locationCoordinateSeverity(store: PosmProjectStore): string {
        return store.location?.location?.latitude != null && store.location?.location?.longitude != null
            ? 'success'
            : 'warning';
    }

    storeIssueCount(store: PosmProjectStore): number {
        return this.validationIssues().filter(issue => issue.resourceId === store.id).length;
    }

    attachmentCategoryLabel(category?: string): string {
        const code = `${category || ''}`.trim().toUpperCase();
        return this.attachmentCategories().find(row => `${row.code || ''}`.trim().toUpperCase() === code)?.name
            || category
            || 'Other';
    }

    attachmentFileKind(attachment: PosmProjectAttachment): string {
        return attachment.fileType
            || attachment.fileName?.split('.').pop()?.toUpperCase()
            || 'Attachment';
    }

    projectJobTypeGroup(project: PosmProject): string {
        const normalized = this.normalizeStatus(project.jobTypeGroup || this.projectJobTypes(project)[0] || project.jobType).toLowerCase();
        if (['installation', 'delivery', 'removal', 'collection'].includes(normalized)) {
            return 'Logistics';
        }
        if (['refurbishment', 'rebranding'].includes(normalized)) {
            return 'Refurbishment and Rebranding';
        }
        if (normalized === 'site survey' || normalized === 'site_survey') {
            return 'Site Survey';
        }
        return project.jobTypeGroup || 'Project';
    }

    projectJobTypes(project: PosmProject): string[] {
        const scopes = (project.jobTypes || [])
            .map(scope => this.normalizeJobTypeLabel(scope))
            .filter(Boolean);
        if (scopes.length) {
            return Array.from(new Set(scopes));
        }
        const legacy = this.normalizeJobTypeLabel(project.jobType);
        return legacy ? [legacy] : [];
    }

    projectJobTypeLabel(project: PosmProject): string {
        const scopes = this.projectJobTypes(project);
        return scopes.length ? scopes.join(' + ') : project.jobType || project.jobTypeGroup || 'General';
    }

    private normalizeJobTypeLabel(value: unknown): string {
        const normalized = this.normalizeStatus(value).toLowerCase();
        switch (normalized) {
            case 'installation':
                return 'Installation';
            case 'delivery':
                return 'Delivery';
            case 'removal':
                return 'Removal';
            case 'collection':
                return 'Collection';
            case 'refurbishment':
                return 'Refurbishment';
            case 'rebranding':
                return 'Rebranding';
            case 'site survey':
            case 'site_survey':
                return 'Site Survey';
            default:
                return `${value || ''}`.trim();
        }
    }

    itemName(item: PosmProjectItem): string {
        return item.inventoryItem?.itemName || item.missingItemReference || 'Unmapped item';
    }

    itemCodeLine(item: PosmProjectItem): string {
        return [
            item.inventoryItem?.itemNumber,
            item.inventoryItem?.skuNumber ? `SKU ${item.inventoryItem.skuNumber}` : null,
            item.inventoryItem?.customerItemNumber ? `Customer ${item.inventoryItem.customerItemNumber}` : null
        ].filter(Boolean).join(' | ');
    }

    itemPhysicalMeta(item: PosmProjectItem): string {
        const inventory = item.inventoryItem || {};
        const unit = inventory.lengthUnitName || 'm';
        const hasDimensions = [inventory.lengthMeters, inventory.breadthMeters, inventory.heightMeters].some(value => value != null);
        const size = hasDimensions
            ? `Size ${this.numberValue(inventory.lengthMeters)} x ${this.numberValue(inventory.breadthMeters)} x ${this.numberValue(inventory.heightMeters)} ${unit}`
            : null;
        const uom = inventory.tenantUomName ? `UOM ${inventory.tenantUomName}` : null;
        const weight = inventory.weightKg != null ? `Weight ${this.numberValue(inventory.weightKg)} kg` : null;
        const sqm = inventory.sqm != null ? `SQM ${this.numberValue(inventory.sqm)}` : null;
        const cbm = inventory.cbm != null ? `CBM ${this.numberValue(inventory.cbm)}` : null;
        return [uom, size, weight, sqm, cbm].filter(Boolean).join(' | ') || 'No physical metadata';
    }

    itemHierarchyMeta(item: PosmProjectItem): string {
        const inventory = item.inventoryItem || {};
        return [
            inventory.hierarchyPath || inventory.categoryName,
            inventory.brandName,
            inventory.subBrandName,
            inventory.trackingMode
        ].filter(Boolean).join(' | ') || 'No hierarchy linked';
    }

    materialStatusSeverity(status?: string): string {
        const normalized = this.normalizeStatus(status);
        if (normalized === 'ALLOCATED' || normalized === 'RESERVED') return 'success';
        if (normalized === 'PARTIAL') return 'warn';
        if (normalized === 'UNRESERVED' || normalized === 'NOT_ALLOCATED') return 'danger';
        return 'info';
    }

    scheduleAllocationSeverity(status?: string): string {
        const normalized = this.normalizeStatus(status);
        if (normalized === 'ALLOCATED') return 'success';
        if (normalized === 'PARTIAL') return 'warn';
        return 'danger';
    }

    scheduleProgressSeverity(row: PosmDeliverySchedule): string {
        if (this.isDelivered(row)) return 'success';
        if (this.isDelayedSchedule(row)) return 'danger';
        if (this.isReleased(row) || this.isActiveTrackingRow(row)) return 'info';
        return 'secondary';
    }

    scheduleProgressLabel(row: PosmDeliverySchedule): string {
        return row.progressStage || row.shipmentStatus || row.transportStatus || row.taskStatus || row.status || 'PLANNED';
    }

    formattedDate(value: unknown): string {
        if (!value) return '-';
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
    }

    formattedDateTime(value: unknown): string {
        if (!value) return '-';
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
    }

    scheduleStoreLabel(row: PosmDeliverySchedule): string {
        return row.storeName
            || row.store?.location?.name
            || row.store?.storeName
            || row.location?.name
            || row.city
            || 'Store pending';
    }

    scheduleStoreMeta(row: PosmDeliverySchedule): string {
        const installationDate = row.plannedInstallationDateOverride || row.plannedInstallationDate || row.installationDate || row.jobDate;
        return [
            row.scheduleNumber,
            row.storeNumber ? `Store ${row.storeNumber}` : null,
            installationDate ? `Install ${this.formattedDate(installationDate)}` : null
        ].filter(Boolean).join(' | ');
    }

    scheduleItemLabel(row: PosmDeliverySchedule): string {
        return row.inventoryItem?.itemName
            || row.projectItem?.inventoryItem?.itemName
            || row.standName
            || 'Material pending';
    }

    scheduleItemMeta(row: PosmDeliverySchedule): string {
        return [
            row.inventoryItem?.itemNumber || row.itemSku,
            row.inventoryItem?.skuNumber ? `SKU ${row.inventoryItem.skuNumber}` : null,
            row.inventoryItem?.customerItemNumber ? `Customer ${row.inventoryItem.customerItemNumber}` : null,
            row.allocatedQuantity != null ? `Qty ${this.numberValue(row.allocatedQuantity)}` : null
        ].filter(Boolean).join(' | ');
    }

    daysRemaining(): number {
        if (!this.project().deadline) return 0;
        const deadline = new Date(this.project().deadline!);
        const today = new Date();
        const diffTime = deadline.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    }

    private itemReservedQuantity(itemId?: number): number {
        if (!itemId) return 0;
        return (this.projectItemSources()[itemId] || []).reduce((total, source) => total + this.numberValue(source.reservedQuantity), 0);
    }

    private itemAllocatedQuantity(itemId?: number): number {
        if (!itemId) return 0;
        return this.deliverySchedules()
            .filter(row => row.projectItemId === itemId)
            .reduce((total, row) => total + this.numberValue(row.allocatedQuantity), 0);
    }

    private materialRowStatus(item: PosmProjectItem, requested: number, reserved: number, allocated: number): string {
        if (requested > 0 && allocated >= requested) return 'ALLOCATED';
        if (requested > 0 && reserved >= requested) return 'RESERVED';
        if (reserved > 0 || allocated > 0) return 'PARTIAL';
        return item.materialStatus || 'UNRESERVED';
    }

    private materialRowDetail(status: string, gap: number, requested: number, reserved: number, allocated: number): string {
        switch (this.normalizeStatus(status)) {
            case 'ALLOCATED':
                return `All ${requested} requested units are allocated into delivery schedules.`;
            case 'RESERVED':
                return `${reserved} covered units are ready for schedule allocation.`;
            case 'PARTIAL':
                return `${gap} units still need source coverage or schedule allocation attention.`;
            default:
                return `${allocated} allocated | ${reserved} covered | ${gap} gap remaining.`;
        }
    }

    private isReleased(row: PosmDeliverySchedule): boolean {
        return !!row.transportReleaseId
            || !!row.releaseNumber
            || !['', 'NOT_RELEASED'].includes(this.normalizeStatus(row.releaseStatus));
    }

    private isDelivered(row: PosmDeliverySchedule): boolean {
        return this.normalizeStatus(row.progressStage) === 'DELIVERED';
    }

    private isActiveTrackingRow(row: PosmDeliverySchedule): boolean {
        if (this.isDelivered(row)) return false;
        const stage = this.normalizeStatus(row.progressStage || row.transportStatus || row.shipmentStatus);
        return !!row.shipmentId
            || !!row.deliveryId
            || !!row.deliveryNumber
            || !!row.assignedDriverId
            || !!row.truckName
            || ['TRUCK_PLANNED', 'IN_TRANSIT', 'TASK_CREATED', 'SCHEDULED', 'RELEASED'].includes(stage);
    }

    private isDelayedSchedule(row: PosmDeliverySchedule): boolean {
        const due = this.normalizeStatus(row.dueStatus);
        const stage = this.normalizeStatus(row.progressStage || row.transportStatus || row.shipmentStatus);
        return due === 'OVERDUE'
            || stage === 'EXCEPTION'
            || stage === 'DELAYED'
            || stage === 'LATE';
    }

    private scheduleRiskScore(row: PosmDeliverySchedule): number {
        let score = 0;
        if (this.normalizeStatus(row.readinessStatus) !== 'READY') score += 4;
        if (this.normalizeStatus(row.allocationStatus) !== 'ALLOCATED') score += 3;
        if (!this.isReleased(row)) score += 2;
        if (this.isDelayedSchedule(row)) score += 3;
        if ((row.mappingWarnings || []).length || (row.allocationWarnings || []).length) score += 2;
        return score;
    }

    private money(value?: number | null): string {
        const amount = Number(value || 0);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'SAR',
            maximumFractionDigits: 0
        }).format(amount);
    }

    private statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        const normalized = this.normalizeStatus(status);
        if (['CLOSED', 'COMPLETED', 'DELIVERED'].includes(normalized)) return 'success';
        if (['NEW', 'DRAFT', 'PENDING'].includes(normalized)) return 'warn';
        if (['CANCELLED', 'REJECTED', 'ERROR'].includes(normalized)) return 'danger';
        if (!normalized) return 'secondary';
        return 'info';
    }

    private auditActorLabel(
        actor?: AuditActorSummary | null,
        actorId?: number | null,
        fallback = '-'): string {
        if (actor?.name) {
            return actor.id ? `${actor.name} (#${actor.id})` : actor.name;
        }
        if (actorId != null) {
            return `#${actorId}`;
        }
        return fallback;
    }

    private auditBadge(project: PosmProject): string {
        if (project.reopenedAt) return 'REOPENED';
        if (project.closedAt || this.normalizeStatus(project.closureStatus) === 'CLOSED') return 'CLOSED';
        if (project.updatedAt) return 'TRACKED';
        return 'NEW RECORD';
    }

    private auditBadgeSeverity(project: PosmProject): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (project.reopenedAt) return 'warn';
        if (project.closedAt || this.normalizeStatus(project.closureStatus) === 'CLOSED') return 'success';
        if (project.updatedAt) return 'info';
        return 'secondary';
    }

    private auditTone(project: PosmProject): 'default' | 'success' | 'warn' | 'danger' | 'info' {
        if (project.reopenedAt) return 'warn';
        if (project.closedAt || this.normalizeStatus(project.closureStatus) === 'CLOSED') return 'success';
        if (project.updatedAt) return 'info';
        return 'default';
    }

    private auditNote(project: PosmProject): string {
        if (project.reopenedAt) {
            return 'This execution was reopened after closure and is now back under active control.';
        }
        if (project.closedAt || this.normalizeStatus(project.closureStatus) === 'CLOSED') {
            return 'Closure ownership is recorded and the execution has a final audit state.';
        }
        return 'Use this trail to confirm who created the record and who last changed the execution workspace.';
    }

    private normalizeSchedule(row: PosmDeliverySchedule): PosmDeliverySchedule {
        return {
            ...row,
            releaseGeneratedAt: this.toDate(row.releaseGeneratedAt),
            releasedToTruckPlanAt: this.toDate(row.releasedToTruckPlanAt),
            actualDeliveryDate: this.toDate(row.actualDeliveryDate),
            plannedInstallationDate: this.toDate(row.plannedInstallationDate),
            plannedInstallationDateOverride: this.toDate(row.plannedInstallationDateOverride),
            installationDate: this.toDate(row.installationDate),
            jobDate: this.toDate(row.jobDate),
            statusDate: this.toDate(row.statusDate)
        };
    }

    private numberValue(value: unknown): number {
        const parsed = Number(value || 0);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private normalizeStatus(value: unknown): string {
        return `${value || ''}`.trim().toUpperCase();
    }

    private toOptions(rows: PosmSetupData[]) {
        return rows
            .filter(row => row.isActive !== false)
            .map(row => ({ label: row.name, value: row.code || row.name }));
    }

    private locationOptionLabel(location: any): string {
        const name = location?.name || `Location #${location?.id || '-'}`;
        const reference = location?.externalLocationReference ? ` - ${location.externalLocationReference}` : '';
        const type = location?.locationType ? ` (${location.locationType})` : '';
        return `${name}${reference}${type}`;
    }

    private locationAddressFromLocation(location: any): string {
        const address = location?.address;
        if (typeof address === 'string') return address;
        return address?.fullAddress
            || location?.longDescription
            || location?.shortDescription
            || '';
    }

    private toDate(value: unknown): Date | undefined {
        if (!value) return undefined;
        const date = value instanceof Date ? value : new Date(value as string);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
}
